import { useRouter } from 'next/router';
import React, { useState } from 'react';
import { Text, Stack, Flex, Icon } from '@pairy/pds-react';
import { useLocalStorage } from '@pairy/react-hooks';
import { useAtomValue } from 'jotai';

import { SortType } from '@/hooks/queries/client/useClientList';
import useClientTrackList, {
  TrackingLinkClient,
  isLockedItem,
} from '@/hooks/queries/client/useClientTrackList';
import LockedClientRowPlaceholder from '@/components/User/_components/common/LockedClientRowPlaceholder';
import useFile from '@/hooks/queries/file/useFile';
import SortTypeDropdown from '@/components/User/_components/common/SortTypeDropdown';
import CSVDownloadButton from '@/components/User/_components/common/CSVDownloadButton';
import { localStorageKey } from '@/constants/localStorageKey';
import { getUTCDate } from '@/utils/getUTCDate';
import { getFileShareLink, getShortShareLink } from '@/utils/shareLink';
import { formatKoreanPhoneNumber } from '@/utils/formatPhone';
import { userInfoAtom } from '@/state/user/atom';
import PaywalledBlur from '@/components/User/_components/pdf/PaywalledBlur';
import ProPlanUpgradeModal from '@/components/User/_components/common/ProPlanUpgradeModal';
import BlurredCompactItem from '@/components/User/_components/common/BlurredCompactItem';
import useShortShareTokenBatch, {
  shortShareTokenCacheKey,
} from '@/hooks/useShortShareTokenBatch';

import Box from './Box';

const items = [
  { label: '최신 생성일 순', value: 'latestCreation' },
  { label: '자주 열람한 순', value: 'most' },
  { label: '최근 열람 시간 순', value: 'recent' },
  { label: '고객 대표명 순', value: 'name' },
] as const;

const OneToOne = () => {
  const router = useRouter();
  const userInfo = useAtomValue(userInfoAtom);

  const [sortType, setSortType] = useLocalStorage<SortType>(
    localStorageKey.fileOrLinkDropdown,
    'latestCreation',
  );

  const fileId = Number(router.query.fileId);

  const { data: fileData } = useFile(
    { fileId },
    {
      enabled: Boolean(fileId),
      select: (data) => data.data.data.file,
    },
  );

  const { data: clientTrackListData } = useClientTrackList(
    {
      type: 'file',
      id: fileId,
      sort: sortType ?? 'most',
    },
    {
      enabled: Boolean(fileId) && fileData != null && !fileData.isBulk,
      select: (data) => data.data.data.clients,
      keepPreviousData: true,
    },
  );

  const { tokens, issueTokens, isIssuing } = useShortShareTokenBatch();

  const csvRows = React.useMemo(
    () =>
      clientTrackListData?.filter(
        (item): item is TrackingLinkClient => !isLockedItem(item),
      ) ?? [],
    [clientTrackListData],
  );

  const csvData = React.useMemo(
    () =>
      csvRows.map((client, index) => ({
        '열람 ID': index + 1,
        '고객 이름':
          client.name ||
          client.company ||
          (client.phone ? formatKoreanPhoneNumber(client.phone) : null) ||
          '(이름 없음)',
        이메일: client.email ?? '-',
        '열람 시간': client.lastAction
          ? getUTCDate(client.lastAction, 'withYear')
          : '-',
        '열람 횟수': client.readCount,
        '공유 링크': (() => {
          const token =
            tokens[
              shortShareTokenCacheKey({
                targetType: 'CLIENT_FILE',
                hash: client.hashId,
              })
            ];

          /* 발급에 실패한 행은 기존 URL로 폴백한다. 기존 URL도 계속 유효하다. */
          return token
            ? getShortShareLink({ token, domain: userInfo?.domain })
            : getFileShareLink({
                hashId: client.hashId,
                isBulk: false,
                domain: userInfo?.domain,
              });
        })(),
      })),
    [csvRows, tokens, userInfo?.domain],
  );

  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const firstBlurredIndex = (clientTrackListData ?? []).findIndex(
    (item) => !isLockedItem(item) && item.isBlurred,
  );

  if (!fileData || !clientTrackListData) {
    return null;
  }

  return (
    <>
      <Stack direction="column" gap={16}>
        <Flex alignItems="center" justifyContent="space-between">
          <Flex display="inline-flex" alignItems="center">
            <Text as="p" variant="lg-semibold" marginRight={16}>
              이 파일을 받은 고객
            </Text>
            {clientTrackListData.length !== 0 && (
              <CSVDownloadButton
                filename={`${fileData.name}_pdf파일_고객목록-${getUTCDate(
                  '',
                  'yearMonthDay2',
                )}`}
                csvData={csvData}
                isLoading={isIssuing}
                onClick={() =>
                  issueTokens(
                    csvRows.map((client) => ({
                      targetType: 'CLIENT_FILE' as const,
                      hash: client.hashId,
                    })),
                  )
                }
              >
                고객 링크 다운로드 (CSV)
              </CSVDownloadButton>
            )}
          </Flex>
          <Flex display="inline-flex" alignItems="center">
            <SortTypeDropdown
              initialValue={sortType}
              items={items}
              onValueChanges={(value) => setSortType(value)}
            />
          </Flex>
        </Flex>
        {clientTrackListData.length === 0 ? (
          <Flex direction="column" alignItems="center" marginTop={16}>
            <Icon icon="ph:user-focus" size={48} color="zinc_400" />
            <Text
              as="p"
              variant="2xl-semibold"
              color="zinc_400"
              marginTop={16}
              textAlign="center"
            >
              왼쪽 버튼을 눌러 문서 공유 링크를 생성해보세요.
            </Text>
          </Flex>
        ) : (
          <Stack direction="column" gap={8}>
            {clientTrackListData?.map((clientTrackItem, index) => {
              if (isLockedItem(clientTrackItem)) {
                return (
                  <BlurredCompactItem
                    key={`locked-${index}`}
                    onUpgradeClick={() => setIsUpgradeModalOpen(true)}
                    showBorder
                  >
                    <LockedClientRowPlaceholder />
                  </BlurredCompactItem>
                );
              }

              return clientTrackItem.isBlurred ? (
                index === firstBlurredIndex ? (
                  <PaywalledBlur
                    key={clientTrackItem.id}
                    title="최근 7일 데이터만 확인할 수 있어요"
                    description="업그레이드하면 전체 기간 데이터를 확인할 수 있어요."
                    onUpgradeClick={() => setIsUpgradeModalOpen(true)}
                  >
                    <Box {...clientTrackItem} />
                  </PaywalledBlur>
                ) : (
                  <BlurredCompactItem
                    key={clientTrackItem.id}
                    onUpgradeClick={() => setIsUpgradeModalOpen(true)}
                  >
                    <Box {...clientTrackItem} />
                  </BlurredCompactItem>
                )
              ) : (
                <Box key={clientTrackItem.id} {...clientTrackItem} />
              );
            })}
          </Stack>
        )}
      </Stack>
      {isUpgradeModalOpen && (
        <ProPlanUpgradeModal onOpenChange={setIsUpgradeModalOpen} />
      )}
    </>
  );
};

export default OneToOne;
